// src/components/insightsDiscovery/components/insightsDiscoveryMain.js
import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import {
  getRuns,
  setActiveRun,
  setActiveAnnotation,
  executeAnnotation,
  getModels,
  setSelectedModel,
  getRunById,
} from "../reducers/insightsDiscoveryReducer";
import { addTabToNav } from "../../topLevelNav/reducers/topLevelNavReducer";
import withRouter from "../../../hocs/withRouter";
import { renderMarkdownLine, renderMarkdownContent, parseMarkdownTable, renderMarkdownTableElement, slugify } from "../../../lib/markdownRenderer";
import { ThumbsUpIcon, ThumbsDownIcon } from "../../../lib/icons";
import AGGridTable from "../../snowflakeCortexChat/AGGridTable";
import { toast } from "react-toastify";
import "../insightsDiscovery.css";

const SERVER = process.env.REACT_APP_BIGHORN_SERVER;
const authHeaders = () => ({ ClientToken: localStorage.getItem("clientToken") });

const CATEGORY_LABELS = {
  findings: "Findings",
  "data-points": "Data Points",
  methodology: "Methodology",
  "follow-ups": "Follow-Ups",
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/**
 * Convert bighorn-server result (columns: [{name,type}], rows: [{}]) into
 * the headers/rows/isNumericColumn/columnFormats shape that AGGridTable expects.
 */
/**
 * Parse inline text into segments: plain text, bold, and citation markers.
 * Citations are [key_name] where key_name contains letters/digits/underscores/hyphens
 * but is NOT purely numeric (those are treated as plain text to avoid false matches).
 * Returns array of { type, content?, key? }
 */
function parseInlineTokens(text) {
  const parts = [];
  // Match **bold** and [memory_key] citations (non-numeric keys only)
  const tokenRegex = /(\*\*[^*]+\*\*|\[[a-zA-Z][a-zA-Z0-9_-]*\])/g;
  let lastIndex = 0;
  let match;
  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.substring(lastIndex, match.index) });
    }
    const token = match[0];
    if (token.startsWith("**")) {
      parts.push({ type: "bold", content: token.slice(2, -2) });
    } else {
      // Extract key name without brackets
      parts.push({ type: "citation", key: token.slice(1, -1) });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.substring(lastIndex) });
  }
  return parts;
}

/**
 * Scan synthesis text and build an ordered map of citation key → sequential number.
 * Keys are numbered in order of first appearance.
 */
function buildCitationIndex(text) {
  const keyToNum = new Map();
  const keyRegex = /\[([a-zA-Z][a-zA-Z0-9_-]*)\]/g;
  let match;
  while ((match = keyRegex.exec(text)) !== null) {
    const key = match[1];
    if (!keyToNum.has(key)) {
      keyToNum.set(key, keyToNum.size + 1);
    }
  }
  return keyToNum;
}

/**
 * Find the annotation whose memoryKeys array (or memoryKey) includes the given key.
 */
function findAnnotationByKey(annotations, key) {
  return annotations.find(
    (a) =>
      (Array.isArray(a.memoryKeys) && a.memoryKeys.includes(key)) ||
      a.memoryKey === key,
  ) || null;
}

/**
 * Render a single line's inline tokens.
 * Citations render as a numbered superscript + inline expandable box at click position.
 * keyCounts is a Map<key, number> tracking how many times each key has appeared so far
 * across all lines — mutated in place so each occurrence gets a unique instanceId.
 */
function renderInlineTokens(
  parts, annotations, keyToNum, expandedCitations,
  onToggleCitation, onCitationExecute, citationResults, citationInterpOpen, onToggleCitationInterp,
  keyCounts,
) {
  return parts.map((part, idx) => {
    if (part.type === "bold") return <strong key={idx}>{part.content}</strong>;
    if (part.type === "citation") {
      const ann = findAnnotationByKey(annotations, part.key);
      if (!ann) return null;
      const num = keyToNum.get(part.key) ?? "?";
      // Unique instance ID so each occurrence toggles independently
      const occurrence = keyCounts.get(part.key) ?? 0;
      keyCounts.set(part.key, occurrence + 1);
      const instanceId = `${part.key}:${occurrence}`;
      const isOpen = expandedCitations.has(instanceId);
      const citResult = citationResults[instanceId];
      return (
        <span key={idx} className="id-citation-wrapper">
          <sup>
            <span
              className="id-citation-ref"
              title={ann.question}
              onClick={(e) => { e.stopPropagation(); onToggleCitation(instanceId); }}>
              {num}
            </span>
          </sup>
          {isOpen && (
            <span className="id-inline-citation-box">
              <span className="id-inline-citation-header" onClick={(e) => { e.stopPropagation(); onToggleCitation(instanceId); }}>
                <span className="id-inline-citation-label">
                  <span className="id-citation-ref" style={{ verticalAlign: "middle", marginRight: "6px", flexShrink: 0 }}>{num}</span>
                  {ann.question}
                </span>
                <button className="id-inline-citation-toggle" type="button">Hide Details</button>
              </span>
              <span className="id-inline-citation-content">
                {/* Synthesized reference — show the summary only, no live query to run */}
                {ann.derived && (
                  <>
                    <span className="id-inline-citation-narrative">
                      {renderMarkdownContent(ann.resultPreview || ann.question || "")}
                    </span>
                    <span className="id-inline-citation-derived-note">
                      Synthesized from multiple queries — no single live query to run.
                    </span>
                  </>
                )}

                {/* Loading */}
                {!ann.derived && citResult?.loading && (
                  <span className="id-inline-citation-loading">Running query…</span>
                )}

                {/* Error */}
                {!ann.derived && !citResult?.loading && citResult?.error && (
                  <span className="id-inline-citation-error">{citResult.error}</span>
                )}

                {/* Live results */}
                {!ann.derived && !citResult?.loading && citResult?.data && (() => {
                  const grids = resultTablesFor(citResult.data)
                    .map((table) => ({ table, gridData: transformResultForGrid(table) }))
                    .filter((g) => g.gridData);
                  const interp = citResult.data.interpretation;
                  const interpOpen = citationInterpOpen.has(instanceId);
                  return (
                    <>
                      {interp && (
                        <span className="id-inline-citation-interp-section">
                          <span
                            className="id-inline-citation-interp-toggle"
                            onClick={(e) => { e.stopPropagation(); onToggleCitationInterp(instanceId); }}>
                            {interpOpen ? "▾" : "▸"} SQL Explanation
                          </span>
                          {interpOpen && (
                            <span className="id-inline-citation-interp-body">{renderMarkdownContent(interp)}</span>
                          )}
                        </span>
                      )}
                      {grids.length === 0 && citResult.data.narrative && (
                        <span className="id-inline-citation-narrative">
                          {renderMarkdownContent(citResult.data.narrative)}
                        </span>
                      )}
                      {grids.map(({ table, gridData }, ti) => (
                        <React.Fragment key={ti}>
                          <span className="id-inline-result-meta">
                            {table.rowCount} row{table.rowCount !== 1 ? "s" : ""}
                            {table.executionTimeMs != null && ` · ${(table.executionTimeMs / 1000).toFixed(1)}s`}
                          </span>
                          <span className="id-inline-grid-wrapper">
                            <AGGridTable
                              headers={gridData.headers}
                              rows={gridData.rows}
                              isNumericColumn={gridData.isNumericColumn}
                              columnFormats={gridData.columnFormats}
                            />
                          </span>
                        </React.Fragment>
                      ))}
                    </>
                  );
                })()}

                {!ann.derived && (
                  <button
                    className="id-inline-citation-run-btn"
                    disabled={citResult?.loading}
                    onClick={(e) => { e.stopPropagation(); onCitationExecute(ann, instanceId); }}>
                    {citResult?.loading ? "Running…" : citResult?.data ? "Re-run Query" : "Run Live Query →"}
                  </button>
                )}
              </span>
            </span>
          )}
        </span>
      );
    }
    return <span key={idx}>{part.content}</span>;
  });
}

/**
 * Render synthesis text as markdown lines with numbered inline citation boxes.
 */
function renderSynthesisContent(
  text, annotations, expandedCitations, onToggleCitation,
  onCitationExecute, citationResults, citationInterpOpen, onToggleCitationInterp,
) {
  if (!text) return null;
  const keyToNum = buildCitationIndex(text);
  const keyCounts = new Map();
  const renderFn = (line) =>
    renderInlineTokens(
      parseInlineTokens(line), annotations, keyToNum, expandedCitations,
      onToggleCitation, onCitationExecute, citationResults, citationInterpOpen, onToggleCitationInterp,
      keyCounts,
    );
  const parts = parseMarkdownTable(text);
  const elements = [];
  let inToc = false;
  parts.forEach((part, partIdx) => {
    if (part.type === "table") {
      inToc = false;
      elements.push(renderMarkdownTableElement(part, `table-${partIdx}`));
    } else {
      part.content.split("\n").forEach((line, lineIdx) => {
        const key = `${partIdx}-${lineIdx}`;
        if (line.startsWith("## ") && line.toLowerCase().includes("table of contents")) {
          inToc = true;
          elements.push(renderMarkdownLine(line, key, renderFn));
          return;
        }
        if (/^#{1,3} /.test(line) && inToc) {
          inToc = false;
        }
        if (inToc && line.startsWith("- ")) {
          const title = line.substring(2).trim();
          const id = slugify(title);
          elements.push(
            <div key={key} style={{ marginLeft: "20px", marginBottom: "4px" }}>
              {"• "}
              <a
                href={`#${id}`}
                style={{ color: "#2e5ca6", textDecoration: "none" }}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(id);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                {renderFn(title)}
              </a>
            </div>
          );
          return;
        }
        elements.push(renderMarkdownLine(line, key, renderFn));
      });
    }
  });
  return <div>{elements}</div>;
}

function transformResultForGrid(queryResult) {
  if (!queryResult || !queryResult.columns || !queryResult.rows) {
    return null;
  }
  const headers = queryResult.columns.map((c) => c.name);
  const NUMERIC_TYPES = new Set(["fixed", "real", "float", "integer", "number"]);
  const isNumericColumn = queryResult.columns.map((c) =>
    NUMERIC_TYPES.has((c.type || "").toLowerCase()),
  );
  const columnFormats = headers.map(() => "");
  const rows = queryResult.rows.map((row) =>
    headers.map((h) => (row[h] !== undefined ? row[h] : null)),
  );
  return { headers, rows, isNumericColumn, columnFormats };
}

/**
 * Normalize an executeAnnotation response into a list of result tables.
 * Multi-part questions are answered by several SQL statements — the server
 * returns one table per statement in `tables`; older responses and the
 * agent/Analyst fallback paths return a single top-level table.
 */
function resultTablesFor(queryResult) {
  if (!queryResult) return [];
  if (Array.isArray(queryResult.tables) && queryResult.tables.length > 0) {
    return queryResult.tables;
  }
  return [queryResult];
}

class InsightsDiscoveryMain extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showSQL: false,
      expandedCitations: new Set(),
      citationResults: {},       // instanceId → { loading, data, error }
      citationInterpOpen: new Set(), // instanceIds with interpretation expanded
      feedback: { rating: null, showTextInput: false, comment: "", isSubmitting: false, submitted: false, error: null },
    };
  }

  componentDidMount() {
    this.props.addTabToNav({
      title: "Insights Discovery",
      name: "Insights Discovery",
      id: "insightsDiscovery",
      type: "insightsDiscovery",
      path: "/insightsDiscovery/runs",
      icon: "insightsDiscovery",
      activeChildren: [],
    });
    if (!this.props.runsLoaded && !this.props.runsLoading) {
      this.props.getRuns();
    }
    this.props.getModels();
    // If runs are already loaded (e.g. navigating back), select from URL immediately
    if (this.props.runsLoaded && this.props.params?.runId) {
      this._selectRunFromUrl(this.props.params.runId);
    }
  }

  componentDidUpdate(prevProps) {
    // When runs finish loading, check for a runId in the URL and auto-select it
    const justLoaded = !prevProps.runsLoaded && this.props.runsLoaded;
    if (justLoaded && this.props.params?.runId) {
      this._selectRunFromUrl(this.props.params.runId);
      return;
    }
    // When the URL runId changes (browser back/forward), sync active run
    if (
      prevProps.params?.runId !== this.props.params?.runId &&
      this.props.params?.runId &&
      this.props.runsLoaded
    ) {
      this._selectRunFromUrl(this.props.params.runId);
    }
  }

  _selectRunFromUrl(runId) {
    const run = this.props.runs.find((r) => r._id === runId);
    if (run) {
      if (!this.props.activeRun || this.props.activeRun._id !== runId) {
        this.props.setActiveRun(run);
        this.setState({
          showSQL: false,
          expandedCitations: new Set(),
          citationResults: {},
          citationInterpOpen: new Set(),
          feedback: { rating: null, showTextInput: false, comment: "", isSubmitting: false, submitted: false, error: null },
        });
      }
    } else {
      // Run not in the loaded page — fetch it directly from the server.
      // getRunById will prepend it to the list and activate it.
      this.props.getRunById(runId);
    }
  }

  handleRunClick = (run) => {
    this.props.setActiveRun(run);
    this.setState({
      showSQL: false,
      expandedCitations: new Set(),
      citationResults: {},
      citationInterpOpen: new Set(),
      feedback: { rating: null, showTextInput: false, comment: "", isSubmitting: false, submitted: false, error: null },
    });
    // Push run ID into the URL so the link is shareable and survives reload
    if (this.props.navigate) {
      this.props.navigate(`/insightsDiscovery/runs/${run._id}`, { replace: false });
    }
  };

  handleToggleCitation = (num) => {
    this.setState((s) => {
      const next = new Set(s.expandedCitations);
      if (next.has(num)) next.delete(num); else next.add(num);
      return { expandedCitations: next };
    });
  };

  handleCitationExecute = (ann, instanceId) => {
    const { activeRun } = this.props;
    if (!activeRun) return;
    this.setState((s) => ({
      citationResults: { ...s.citationResults, [instanceId]: { loading: true, data: null, error: null } },
    }));
    axios
      .post(
        `${SERVER}/api/insightsDiscovery/executeAnnotation`,
        { runId: activeRun._id, question: ann.question, annotationOrder: ann.order },
        { headers: authHeaders(), timeout: 120000 },
      )
      .then((res) => {
        this.setState((s) => ({
          citationResults: { ...s.citationResults, [instanceId]: { loading: false, data: res.data, error: null } },
        }));
      })
      .catch((err) => {
        const error = err.response?.data?.error || err.response?.data?.detail || err.message;
        this.setState((s) => ({
          citationResults: { ...s.citationResults, [instanceId]: { loading: false, data: null, error } },
        }));
      });
  };

  handleToggleCitationInterp = (instanceId) => {
    this.setState((s) => {
      const next = new Set(s.citationInterpOpen);
      if (next.has(instanceId)) next.delete(instanceId); else next.add(instanceId);
      return { citationInterpOpen: next };
    });
  };

  handleCitationRunQuery = (annotation) => {
    this.handleAnnotationClick(annotation);
    // Scroll the query result panel into view
    this.setState({ showSynthesis: true });
  };

  handleAnnotationClick = (annotation) => {
    this.props.setActiveAnnotation(annotation);
    this.setState({ showSQL: false });
    // Auto-execute when clicking an annotation
    if (this.props.activeRun) {
      this.props.executeAnnotation(this.props.activeRun._id, annotation.question, annotation.order);
    }
  };

  handleExecute = () => {
    const { activeRun, activeAnnotation } = this.props;
    if (!activeRun || !activeAnnotation) return;
    this.props.executeAnnotation(activeRun._id, activeAnnotation.question, activeAnnotation.order);
  };

  handleFeedbackRate = (rating) => {
    this.setState((s) => {
      if (s.feedback.submitted || s.feedback.isSubmitting) return null;
      return { feedback: { ...s.feedback, rating, showTextInput: true, error: null } };
    });
  };

  handleFeedbackComment = (comment) => {
    this.setState((s) => ({ feedback: { ...s.feedback, comment } }));
  };

  cancelFeedback = () => {
    this.setState((s) => ({ feedback: { ...s.feedback, rating: null, showTextInput: false, comment: "", error: null } }));
  };

  submitFeedback = async () => {
    const { activeRun } = this.props;
    const { feedback } = this.state;
    if (!feedback.rating || !activeRun) return;
    this.setState((s) => ({ feedback: { ...s.feedback, isSubmitting: true, error: null } }));
    try {
      await axios.post(
        `${SERVER}/api/insightsDiscovery/feedback`,
        { runId: activeRun._id, rating: feedback.rating, comment: feedback.comment },
        { headers: authHeaders() },
      );
      this.setState((s) => ({ feedback: { ...s.feedback, isSubmitting: false, submitted: true, showTextInput: false } }));
    } catch (err) {
      const error = err.response?.data?.error || err.message || "Failed to submit feedback";
      this.setState((s) => ({ feedback: { ...s.feedback, isSubmitting: false, error } }));
    }
  };

  renderRunList() {
    const { runs, runsLoading, activeRun, models, selectedModel } = this.props;

    const modelSelector = models && models.length > 1 ? (
      <div className="id-model-selector">
        <select
          className="id-model-select"
          value={selectedModel || ""}
          onChange={e => this.props.setSelectedModel(e.target.value || null)}
        >
          <option value="">All Models</option>
          {models.map(m => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>
      </div>
    ) : null;

    if (runsLoading) {
      return <>
        {modelSelector}
        <div className="id-loading-text" style={{ padding: "16px" }}>Loading runs...</div>
      </>;
    }
    if (!runs || runs.length === 0) {
      return (
        <>
          {modelSelector}
          <div className="id-empty-state">
            <div>No completed runs yet.</div>
          </div>
        </>
      );
    }
    return (
      <>
        {modelSelector}
        <div className="id-run-list">
          {runs.map((run) => {
            const modelLabel = run.snowflakeConfigSelection
              ? run.snowflakeConfigSelection.slice(run.snowflakeConfigSelection.indexOf(":") + 1).replace(/_/g, " ")
              : null;
            return (
              <div
                key={run._id}
                className={`id-run-item${activeRun && activeRun._id === run._id ? " active" : ""}`}
                onClick={() => this.handleRunClick(run)}
              >
                <div className="id-run-date">
                  {formatDate(run.completedAt || run.createdAt)}
                  <span className="id-run-time">{formatTime(run.completedAt || run.createdAt)}</span>
                </div>
                {!selectedModel && modelLabel && (
                  <div className="id-run-model-tag">{modelLabel}</div>
                )}
                <div className="id-run-meta">
                  {run.score != null && (
                    <span className="id-run-score">Score {run.score}</span>
                  )}
                  <span>{run.annotationCount || 0} queries</span>
                </div>
                {run.synthesisPreview && (
                  <div className="id-preview-text">{run.synthesisPreview}</div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  renderAnnotationList() {
    const { annotations, annotationsLoading, activeAnnotation } = this.props;
    if (annotationsLoading) {
      return <div className="id-loading-text" style={{ padding: "16px" }}>Loading queries...</div>;
    }
    if (!annotations || annotations.length === 0) {
      return (
        <div className="id-empty-state">
          <div>No annotations for this run.</div>
        </div>
      );
    }
    return (
      <div className="id-annotation-list">
        {annotations.map((ann, idx) => (
          <div
            key={idx}
            className={`id-annotation-item${activeAnnotation && activeAnnotation.order === ann.order ? " active" : ""}`}
            onClick={() => this.handleAnnotationClick(ann)}
          >
            <div className="id-annotation-question">{ann.question}</div>
            <div className={`id-annotation-category ${ann.category || "data-points"}`}>
              {CATEGORY_LABELS[ann.category] || ann.category}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderQueryResult() {
    const { queryResult, queryLoading, queryError, activeAnnotation, activeRun } = this.props;

    if (!activeAnnotation) return null;

    return (
      <div className="id-query-panel">
        <div className="id-query-question">{activeAnnotation.question}</div>

        <button
          className="id-run-btn"
          onClick={this.handleExecute}
          disabled={queryLoading || !activeRun}
        >
          {queryLoading ? "Running..." : "Run Query"}
        </button>

        {queryError && (
          <div className="id-error-box">{queryError}</div>
        )}

        {queryResult && !queryLoading && (
          <>
            {queryResult.interpretation && (
              <div style={{ marginBottom: "12px", fontSize: "13px", color: "#555" }}>
                {renderMarkdownContent(queryResult.interpretation)}
              </div>
            )}

            {(() => {
              const grids = resultTablesFor(queryResult)
                .map((table) => ({ table, gridData: transformResultForGrid(table) }))
                .filter((g) => g.gridData);
              if (grids.length === 0) {
                return queryResult.narrative ? (
                  <div style={{ fontSize: "13px", color: "#555" }}>
                    {renderMarkdownContent(queryResult.narrative)}
                  </div>
                ) : (
                  <div style={{ color: "#888", fontSize: "13px" }}>No results returned.</div>
                );
              }
              return grids.map(({ table, gridData }, ti) => (
                <React.Fragment key={ti}>
                  <div className="id-result-meta">
                    {table.rowCount} row{table.rowCount !== 1 ? "s" : ""}
                    {table.executionTimeMs != null && ` · ${(table.executionTimeMs / 1000).toFixed(1)}s`}
                  </div>
                  {table.rowCount > 0 ? (
                    <AGGridTable
                      headers={gridData.headers}
                      rows={gridData.rows}
                      isNumericColumn={gridData.isNumericColumn}
                      columnFormats={gridData.columnFormats}
                    />
                  ) : (
                    <div style={{ color: "#888", fontSize: "13px" }}>No results returned.</div>
                  )}
                </React.Fragment>
              ));
            })()}
          </>
        )}
      </div>
    );
  }

  renderFeedback() {
    const { activeRun, synthesis, synthesisLoading } = this.props;
    if (!activeRun || synthesisLoading || !synthesis) return null;
    const { rating, showTextInput, comment, isSubmitting, submitted, error } = this.state.feedback;
    return (
      <div className="id-feedback">
        <div className="id-feedback-buttons">
          <span className="id-feedback-label">Was this report helpful?</span>
          <button
            type="button"
            className={`id-feedback-btn${rating === "positive" ? " active" : ""}`}
            onClick={() => this.handleFeedbackRate("positive")}
            disabled={submitted || isSubmitting}
            title="Yes, helpful"
          >
            <ThumbsUpIcon height="16px" width="16px" color={rating === "positive" ? "#2e5ca6" : "#666"} />
          </button>
          <button
            type="button"
            className={`id-feedback-btn${rating === "negative" ? " active" : ""}`}
            onClick={() => this.handleFeedbackRate("negative")}
            disabled={submitted || isSubmitting}
            title="Needs improvement"
          >
            <ThumbsDownIcon height="16px" width="16px" color={rating === "negative" ? "#2e5ca6" : "#666"} />
          </button>
          {submitted && <span className="id-feedback-thanks">Thanks for your feedback!</span>}
          {error && <span className="id-feedback-error">{error}</span>}
        </div>
        {showTextInput && !submitted && (
          <div className="id-feedback-text-container">
            <textarea
              className="id-feedback-textarea"
              placeholder="Optional: add any comments about this report..."
              value={comment}
              onChange={(e) => this.handleFeedbackComment(e.target.value)}
              rows={2}
              disabled={isSubmitting}
            />
            <div className="id-feedback-actions">
              <button
                type="button"
                className="id-feedback-submit"
                onClick={this.submitFeedback}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
              <button
                type="button"
                className="id-feedback-cancel"
                onClick={this.cancelFeedback}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderSynthesis() {
    const { synthesis, synthesisLoading, activeRun, annotations } = this.props;
    const { expandedCitations, citationResults, citationInterpOpen } = this.state;

    if (!activeRun) return null;

    const annotationList = annotations || [];

    return (
      <div className="id-synthesis-section">
        <div className="id-synthesis-body">
          {synthesisLoading && (
            <div className="id-loading-text">Loading report...</div>
          )}
          {!synthesisLoading && synthesis &&
            renderSynthesisContent(
              synthesis,
              annotationList,
              expandedCitations,
              this.handleToggleCitation,
              this.handleCitationExecute,
              citationResults,
              citationInterpOpen,
              this.handleToggleCitationInterp,
            )}
          {!synthesisLoading && !synthesis && (
            <div className="id-loading-text">No report available.</div>
          )}
        </div>
        {this.renderFeedback()}
      </div>
    );
  }

  render() {
    const { activeRun } = this.props;

    return (
      <div className="id-container">
        {/* Sidebar */}
        <div className="id-sidebar">
          <div className="id-sidebar-section">
            <div className="id-sidebar-header">
              <span>Reports</span>
            </div>
            {this.renderRunList()}
          </div>

        </div>

        {/* Main content */}
        <div className="id-content">
          <div className="id-content-header">
            <div className="id-content-header-text">
              <div className="id-content-title">
                {activeRun ? "Insights Discovery" : "Insights Discovery"}
              </div>
              {activeRun && (
                <div className="id-content-subtitle">
                  Run from {formatDate(activeRun.completedAt || activeRun.createdAt)}
                  {activeRun.score != null ? ` · Score ${activeRun.score}/100` : ""}
                </div>
              )}
            </div>
            {activeRun && (
              <a
                className="id-download-docx-btn"
                href="#download"
                onClick={(e) => {
                  e.preventDefault();
                  const headers = { ClientToken: localStorage.getItem("clientToken") };
                  fetch(`${SERVER}/api/insightsDiscovery/downloadDocx?runId=${activeRun._id}`, { headers })
                    .then((r) => {
                      if (!r.ok) throw new Error(`Server error ${r.status}`);
                      return r.blob();
                    })
                    .then((blob) => {
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      const dateStr = new Date(activeRun.completedAt || activeRun.createdAt)
                        .toISOString().slice(0, 10);
                      a.href = url;
                      a.download = `insights-discovery-${dateStr}.docx`;
                      a.click();
                      URL.revokeObjectURL(url);
                    })
                    .catch((err) => toast.error("Download failed"));
                }}
              >
                ↓ Download Report
              </a>
            )}
          </div>

          <div className="id-content-body">
            {this.renderSynthesis()}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  runs: state.insightsDiscovery.runs,
  runsLoading: state.insightsDiscovery.runsLoading,
  runsLoaded: state.insightsDiscovery.runsLoaded,
  activeRun: state.insightsDiscovery.activeRun,
  annotations: state.insightsDiscovery.annotations,
  annotationsLoading: state.insightsDiscovery.annotationsLoading,
  annotationsLoaded: state.insightsDiscovery.annotationsLoaded,
  activeAnnotation: state.insightsDiscovery.activeAnnotation,
  queryResult: state.insightsDiscovery.queryResult,
  queryLoading: state.insightsDiscovery.queryLoading,
  queryError: state.insightsDiscovery.queryError,
  synthesis: state.insightsDiscovery.synthesis,
  synthesisLoading: state.insightsDiscovery.synthesisLoading,
  models: state.insightsDiscovery.models,
  selectedModel: state.insightsDiscovery.selectedModel,
});

const mapDispatchToProps = (dispatch) => ({
  getRuns: (page, modelKey) => dispatch(getRuns(page, modelKey)),
  getModels: () => dispatch(getModels()),
  setSelectedModel: (modelKey) => dispatch(setSelectedModel(modelKey)),
  setActiveRun: (run) => dispatch(setActiveRun(run)),
  getRunById: (runId) => dispatch(getRunById(runId)),
  setActiveAnnotation: (annotation) => dispatch(setActiveAnnotation(annotation)),
  executeAnnotation: (runId, question, annotationOrder) => dispatch(executeAnnotation(runId, question, annotationOrder)),
  addTabToNav: (tab) => dispatch(addTabToNav(tab)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InsightsDiscoveryMain));
